/* ==========================================================================
   Accessibility remediation layer — BUL-260143 WCAG 2.1 audit (May 12, 2026)
   Behaviour that CSS alone cannot provide. Runs after vendor.js/theme.js.
   Written in plain ES5-compatible JS with no jQuery dependency, so it stays
   independent of the theme's bundles.
   ========================================================================== */
(function () {
  'use strict';

  var NAV_ITEM = '.site-nav__item';
  var DISMISSED = 'a11y-flyout-dismissed';

  function closest(el, selector) {
    while (el && el.nodeType === 1) {
      if (el.matches && el.matches(selector)) return el;
      el = el.parentElement;
    }
    return null;
  }

  /* ------------------------------------------------------------------------
     WCAG 1.4.13 (AA) Content on Hover or Focus + WCAG 2.1.1 (A) Keyboard
     accessibility.css opens the flyouts on :focus-within. Escape must be able
     to dismiss one while keeping focus on its trigger, which CSS cannot do, so
     a class overrides :focus-within until focus actually leaves the item.
     ------------------------------------------------------------------------ */

  function setExpanded(item, expanded) {
    var trigger = item.querySelector('.site-nav__link, .meganav__link-toggle');
    if (trigger && trigger.hasAttribute('aria-expanded')) {
      trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }
  }

  function dismissFlyout(item) {
    item.classList.add(DISMISSED);
    setExpanded(item, false);

    var trigger = item.querySelector('.site-nav__link');
    if (trigger) trigger.focus();
  }

  document.addEventListener('keydown', function (event) {
    if (event.key !== 'Escape' && event.keyCode !== 27) return;

    var active = document.activeElement;
    if (!active) return;

    // Close the innermost submenu first, then the top-level flyout.
    var submenu = closest(active, '.site-nav__dropdown-container');
    if (submenu && submenu.querySelector('.site-nav__dropdown')) {
      submenu.classList.add(DISMISSED);
      setExpanded(submenu, false);
      var submenuTrigger = submenu.querySelector('.meganav__link-toggle');
      if (submenuTrigger) {
        submenuTrigger.focus();
        return;
      }
    }

    var item = closest(active, NAV_ITEM);
    if (item) dismissFlyout(item);
  });

  // Clearing the flag on focusout means Tab re-enables normal behaviour, and
  // clearing it on mouseenter keeps the original hover interaction intact.
  document.addEventListener(
    'focusout',
    function (event) {
      var item = closest(event.target, NAV_ITEM);
      if (!item) return;

      // relatedTarget is the element gaining focus.
      if (!item.contains(event.relatedTarget)) {
        item.classList.remove(DISMISSED);
        setExpanded(item, false);

        var nested = item.querySelectorAll('.' + DISMISSED);
        for (var i = 0; i < nested.length; i++) {
          nested[i].classList.remove(DISMISSED);
        }
      }
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (event) {
      var item = closest(event.target, NAV_ITEM);
      if (item && !item.classList.contains(DISMISSED)) setExpanded(item, true);
    },
    true
  );

  var navItems = document.querySelectorAll(NAV_ITEM);
  for (var n = 0; n < navItems.length; n++) {
    navItems[n].addEventListener('mouseenter', function () {
      this.classList.remove(DISMISSED);
    });
  }

  /* ------------------------------------------------------------------------
     WCAG 2.4.1 (A) Bypass Blocks — make the skip link actually skip.
     <main> carries tabindex="-1" so it can take focus, but something in the
     theme's bundles handles in-page anchors and leaves focus on the link, so the
     next Tab returned to the header. Moving focus here is what makes the link
     work; the timeout lets the theme's own scroll handling run first.
     ------------------------------------------------------------------------ */

  var skipLink = document.querySelector('.skip-link');
  if (skipLink) {
    skipLink.addEventListener('click', function () {
      var target = document.getElementById((this.getAttribute('href') || '').replace('#', ''));
      if (!target) return;

      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');
      window.setTimeout(function () {
        target.focus();
      }, 0);
    });
  }

  /* ------------------------------------------------------------------------
     WCAG 4.1.2 — keep aria-pressed in sync on the collection column toggle.
     theme.js only swaps an `active` class, so the ARIA state would go stale.
     ------------------------------------------------------------------------ */

  var viewButtons = document.querySelectorAll('.js-btn-view');
  if (viewButtons.length) {
    var syncViewButtons = function () {
      for (var i = 0; i < viewButtons.length; i++) {
        viewButtons[i].setAttribute(
          'aria-pressed',
          viewButtons[i].classList.contains('active') ? 'true' : 'false'
        );
      }
    };

    for (var v = 0; v < viewButtons.length; v++) {
      // theme.js updates the classes on click; defer so we read them after.
      viewButtons[v].addEventListener('click', function () {
        window.setTimeout(syncViewButtons, 0);
      });
    }
  }

  /* ------------------------------------------------------------------------
     WCAG 4.1.2 — keep aria-expanded in sync on the mini-cart toggle.
     theme.js opens the drawer by putting `mini-cart-open` on <body>, and the
     drawer also opens on add-to-cart and closes from the overlay, so watching
     that class covers every route rather than just a click on the toggle.
     ------------------------------------------------------------------------ */

  var cartToggles = document.querySelectorAll('.js-toggle-cart');
  if (cartToggles.length) {
    var syncCartToggles = function () {
      var open = document.body.classList.contains('mini-cart-open') ? 'true' : 'false';
      for (var i = 0; i < cartToggles.length; i++) {
        if (cartToggles[i].getAttribute('aria-expanded') !== open) {
          cartToggles[i].setAttribute('aria-expanded', open);
        }
      }
    };

    if (window.MutationObserver) {
      new MutationObserver(syncCartToggles).observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }

    syncCartToggles();
  }

  /* ------------------------------------------------------------------------
     WCAG 3.1.2 (AA) Language of Parts — the language switcher's own button.

     The switcher markup is translated in snippets/ly-switcher-factory.liquid,
     but the app names the button itself once its script has run, in English on
     every storefront: "Selected language: Deutsch". It assigns the `ariaLabel`
     property rather than calling setAttribute, and does so again on each
     re-render, so the translated wording is written back whenever it changes.
     ------------------------------------------------------------------------ */

  function nameSwitchers() {
    var strings = (window.themeA11y && window.themeA11y.switcherStrings) || null;
    if (!strings) return;

    var groups = [
      ['.ly-languages-switcher', strings.language_label, strings.language_description],
      ['.ly-country-switcher', strings.country_label, strings.country_description],
    ];

    for (var g = 0; g < groups.length; g++) {
      var switchers = document.querySelectorAll(groups[g][0]);
      for (var i = 0; i < switchers.length; i++) {
        // The wrapper carries the name from Liquid; the button inside it is the
        // one the app writes to, and is what a screen reader actually announces.
        var button = switchers[i].querySelector('.ly-custom-dropdown-current');
        var targets = button ? [switchers[i], button] : [switchers[i]];

        for (var t = 0; t < targets.length; t++) {
          if (groups[g][1] && targets[t].getAttribute('aria-label') !== groups[g][1]) {
            targets[t].setAttribute('aria-label', groups[g][1]);
          }
          if (groups[g][2] && targets[t].getAttribute('aria-description') !== groups[g][2]) {
            targets[t].setAttribute('aria-description', groups[g][2]);
          }
        }
      }
    }
  }

  function watchSwitchers() {
    nameSwitchers();

    if (!window.MutationObserver) return;
    var pending = null;
    var observer = new MutationObserver(function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        nameSwitchers();
      }, 60);
    });

    // The switcher is injected into the body, so watch for it arriving as well
    // as for the app relabelling it afterwards.
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['aria-label', 'aria-description'],
    });
  }

  if (document.body) watchSwitchers();
  else document.addEventListener('DOMContentLoaded', watchSwitchers);

  /* ------------------------------------------------------------------------
     WCAG 2.2.2 (A) Pause, Stop, Hide
     The hero carousel autorotates every 3 seconds with no way to stop it.
     Clearing Slick's `autoplay` option as well as calling slickPause stops
     theme.js's own focus handling from silently restarting the rotation.
     ------------------------------------------------------------------------ */

  /* ------------------------------------------------------------------------
     WCAG 3.1.2 (AA) Language of Parts and 2.4.6 (AA) Headings and Labels
     Slick names its own arrows "Previous"/"Next" and its dots "0" to "7",
     regardless of the storefront language. theme.liquid passes the translated
     wording through window.themeA11y.slideshowStrings.
     ------------------------------------------------------------------------ */

  function nameSlideshowControls(slideshow) {
    var strings = (window.themeA11y && window.themeA11y.slideshowStrings) || null;
    if (!slideshow || !strings) return;

    var arrows = [
      [slideshow.parentNode.querySelector('.slick-prev'), strings.previous],
      [slideshow.parentNode.querySelector('.slick-next'), strings.next],
    ];
    for (var a = 0; a < arrows.length; a++) {
      if (arrows[a][0] && arrows[a][1]) arrows[a][0].setAttribute('aria-label', arrows[a][1]);
    }

    var dots = slideshow.parentNode.querySelectorAll('.slick-dots button');
    for (var d = 0; d < dots.length; d++) {
      // Slick numbers the dots from zero; slide numbers read from one.
      dots[d].setAttribute('aria-label', strings.load_slide.replace('%%N%%', d + 1));
    }
  }

  /* ------------------------------------------------------------------------
     WCAG 2.2.2 (A) Pause, Stop, Hide — where the pause control sits.

     The button is rendered at the end of the slideshow section, which reads
     correctly in the tab order, but its absolute position resolved against the
     page container rather than the carousel, so it drew at the very bottom of the
     page. Moving it into the carousel's own pagination row puts it with the
     controls it belongs to and leaves the tab order as it was, directly after the
     last dot. If the dots are switched off the CSS corner position stands.
     ------------------------------------------------------------------------ */

  function placeSlideshowToggle(toggle, slideshow) {
    if (!toggle || !slideshow || !slideshow.parentNode) return;

    var slider = slideshow.parentNode;
    // Slick throws its dot list away and builds a new one on re-init, so take the
    // last one in the slider rather than the first, which may be on its way out.
    var lists = slider.querySelectorAll('.slick-dots');
    var dots = lists.length ? lists[lists.length - 1] : null;
    if (!dots) return; // dots switched off: the CSS corner position stands

    var row = slider.querySelector('.a11y-slideshow-controls');
    if (!row) {
      row = document.createElement('div');
      row.className = 'a11y-slideshow-controls';
      dots.parentNode.insertBefore(row, dots);
    }
    // The control is a sibling of the dot list, not an item in it: slick marks
    // every dot item it does not consider current aria-hidden="true", which would
    // hide the control from screen readers.
    if (dots.parentNode !== row) row.appendChild(dots);
    if (toggle.parentNode !== row) row.appendChild(toggle);

    // Icon-only once it sits with the arrows and dots, so give mouse users the
    // same wording as a tooltip. Read from the label rather than hard-coded, to
    // keep it in the storefront's language.
    var visible = toggle.querySelector('[data-a11y-label-pause]:not([hidden]), [data-a11y-label-play]:not([hidden])');
    if (visible) toggle.setAttribute('title', visible.textContent.trim());
  }

  /* ------------------------------------------------------------------------
     WCAG 2.4.3 (A) Focus Order — carousel slides that are out of view

     Slick keeps every slide in the DOM and marks the ones off-stage
     `aria-hidden="true"`, but leaves their links tabbable. On the homepage that
     was 17 stops on nothing: six hero slide links collapsed to 0×0, and the
     product tiles, quick-view buttons and Instagram links of the lookbook
     carousel, including its cloned slides. The reviewer saw these as elements
     that take focus without showing anything.
     ------------------------------------------------------------------------ */

  var NATURALLY_FOCUSABLE = 'a[href], button, input, select, textarea, iframe';
  var SLIDE_FOCUSABLE =
    NATURALLY_FOCUSABLE + ', [tabindex]:not([tabindex="-1"]), [data-a11y-tabindex-kept]';

  function untabInactiveSlides() {
    var slides = document.querySelectorAll('.slick-slide');

    for (var i = 0; i < slides.length; i++) {
      var slide = slides[i];
      // A cloned slide is always a duplicate of one that is reachable anyway.
      var offStage =
        slide.getAttribute('aria-hidden') === 'true' ||
        slide.classList.contains('slick-cloned');
      var items = slide.querySelectorAll(SLIDE_FOCUSABLE);

      for (var j = 0; j < items.length; j++) {
        var el = items[j];
        if (offStage) {
          if (!el.hasAttribute('data-a11y-tabindex-kept')) {
            el.setAttribute(
              'data-a11y-tabindex-kept',
              el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : ''
            );
          }
          el.setAttribute('tabindex', '-1');
        } else if (el.hasAttribute('data-a11y-tabindex-kept')) {
          var kept = el.getAttribute('data-a11y-tabindex-kept');
          // Slick parks its own tabindex="-1" on off-stage slides, so what was
          // stored is often that. Handing it back to a slide that is now on stage
          // would undo slick's own work, so anything focusable in its own right
          // simply loses the attribute.
          if (kept === '' || el.matches(NATURALLY_FOCUSABLE)) el.removeAttribute('tabindex');
          else el.setAttribute('tabindex', kept);
          el.removeAttribute('data-a11y-tabindex-kept');
        }
      }
    }
  }

  /* ------------------------------------------------------------------------
     WCAG 2.4.3 (A) Focus Order — the lookbook's quick-view hotspots

     The lookbook carousel drops a `.js-btn-quickview` marker onto the photo for
     each product in the look. They are meant to be little numbered pins, but on
     this store they measure 0×0, so nothing is drawn and there is nothing to
     click — while `tabindex="0"` keeps all twelve of them in the tab order. Every
     product in a look is also listed as a card beside the photo, with the same
     link, so nothing is lost by skipping the pins.
     ------------------------------------------------------------------------ */

  function untabEmptyHotspots() {
    var pins = document.querySelectorAll('.js-btn-quickview');

    for (var i = 0; i < pins.length; i++) {
      var pin = pins[i];
      var rect = pin.getBoundingClientRect();
      var drawn = rect.width > 2 && rect.height > 2;

      if (!drawn) {
        pin.setAttribute('tabindex', '-1');
        pin.setAttribute('aria-hidden', 'true');
      } else if (pin.getAttribute('aria-hidden') === 'true') {
        pin.setAttribute('tabindex', '0');
        pin.removeAttribute('aria-hidden');
      }
    }
  }

  function watchCarousels() {
    if (!window.jQuery) return false;

    var pending = null;
    var resync = function () {
      if (pending) return;
      pending = window.setTimeout(function () {
        pending = null;
        untabInactiveSlides();
        untabEmptyHotspots();
      }, 120);
    };

    // Slick fires these on the slider element and jQuery lets them bubble, so one
    // delegated handler covers every carousel, including ones built later.
    window
      .jQuery(document)
      .on('init reInit afterChange setPosition breakpoint', '.slick-slider', resync);
    resync();
    return true;
  }

  function bindSlideshowToggle() {
    var toggle = document.querySelector('[data-a11y-slideshow-toggle]');
    if (!toggle) return true; // no carousel on this page, nothing to wait for
    if (toggle.getAttribute('data-a11y-bound') === 'true') return true;

    // header-js.liquid loads vendor.js (jQuery and Slick) with `defer`, so it
    // runs after this file. Report back until it has.
    if (!window.jQuery || !window.jQuery.fn || !window.jQuery.fn.slick) return false;

    var $ = window.jQuery;
    var $slideshow = $('#' + toggle.getAttribute('data-a11y-slideshow-toggle'));
    var pauseLabel = toggle.querySelector('[data-a11y-label-pause]');
    var playLabel = toggle.querySelector('[data-a11y-label-play]');

    var setSlideshowPaused = function (paused) {
      if (!$slideshow.length || !$slideshow.hasClass('slick-initialized')) return;

      var slick = $slideshow.slick('getSlick');
      if (slick && slick.options) slick.options.autoplay = !paused;
      $slideshow.slick(paused ? 'slickPause' : 'slickPlay');

      toggle.setAttribute('aria-pressed', paused ? 'true' : 'false');
      if (pauseLabel) pauseLabel.hidden = paused;
      if (playLabel) playLabel.hidden = !paused;

      // The label is hidden while the control sits among the dots, so the tooltip
      // has to follow the state too.
      var current = paused ? playLabel : pauseLabel;
      if (current && toggle.hasAttribute('title')) {
        toggle.setAttribute('title', current.textContent.trim());
      }
    };

    toggle.addEventListener('click', function () {
      setSlideshowPaused(toggle.getAttribute('aria-pressed') !== 'true');
    });
    toggle.setAttribute('data-a11y-bound', 'true');

    nameSlideshowControls($slideshow[0]);
    placeSlideshowToggle(toggle, $slideshow[0]);

    // Slick rebuilds its dots on re-init and on every breakpoint change, which
    // throws the control out again, and it rewrites aria-hidden on the slides on
    // every move.
    $slideshow.on('init reInit breakpoint setPosition afterChange', function () {
      placeSlideshowToggle(toggle, $slideshow[0]);
      nameSlideshowControls($slideshow[0]);
    });

    // Respect the OS-level motion preference on first load.
    var reduceMotion =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduceMotion && reduceMotion.matches) {
      $(function () {
        window.setTimeout(function () {
          setSlideshowPaused(true);
        }, 300);
      });
    }

    return true;
  }

  if (!bindSlideshowToggle()) {
    // Slick is initialised on document ready, some way after vendor.js parses, so
    // poll rather than pick a single moment. Bounded so it cannot run forever on
    // a page where the bundle fails to load.
    var slideshowTries = 0;
    var slideshowTimer = window.setInterval(function () {
      if (bindSlideshowToggle() || ++slideshowTries > 60) {
        window.clearInterval(slideshowTimer);
      }
    }, 250);
  }

  // Separate from the toggle: carousels without a pause control still park links
  // off-stage, on collection and product pages as much as on the homepage.
  if (!watchCarousels()) {
    var carouselTries = 0;
    var carouselTimer = window.setInterval(function () {
      if (watchCarousels() || ++carouselTries > 60) {
        window.clearInterval(carouselTimer);
      }
    }, 250);
  }

  /* ------------------------------------------------------------------------
     WCAG 2.4.3 (A) Focus Order and 4.1.2 (A) Name, Role, Value —
     the header search panel.

     The panel is hidden by transform alone (see accessibility.css), so the CSS
     there takes it out of the tab order. What is left is the behaviour around it:
     the icon has to report whether the panel is open, Escape has to close it, and
     closing has to hand focus back to the icon instead of dropping it. The
     header's own toggleClassSearch() already moves focus into the field on the way
     in, and it lives inline in seven header sections, so it is left alone.
     ------------------------------------------------------------------------ */

  function retryFocus(target, stillWanted) {
    var tries = 0;
    var attempt = function () {
      if (!stillWanted() || document.activeElement === target) return;
      // Reading a layout property flushes the pending style change, so the
      // element is no longer inherit-hidden by the time focus() looks at it.
      void target.offsetWidth;
      target.focus();
      if (document.activeElement !== target && ++tries < 12) {
        window.setTimeout(attempt, 60);
      }
    };
    window.requestAnimationFrame(attempt);
  }

  function enhanceHeaderSearch() {
    var panel = document.getElementById('form-search-header');
    var opener = document.querySelector('.site-header__search-pop');
    if (!panel || !opener) return;

    var field = document.getElementById('SiteNavSearch');
    var isOpen = function () {
      return panel.classList.contains('active');
    };

    opener.setAttribute('aria-expanded', isOpen() ? 'true' : 'false');

    if (window.MutationObserver) {
      new MutationObserver(function () {
        var open = isOpen();
        opener.setAttribute('aria-expanded', open ? 'true' : 'false');

        // The header's own handler calls focus() in the same task as the class
        // change, and the field is still hidden then: it inherits the panel's
        // visibility through its own 0.3s transition, so the call is dropped. How
        // many frames that takes depends on whether a style flush has happened, so
        // retry briefly rather than bet on one.
        if (open && field) retryFocus(field, isOpen);

        // On the way out the focus would otherwise sit inside a panel that is
        // about to be hidden, and end up on <body>.
        if (!open && panel.contains(document.activeElement)) opener.focus();
      }).observe(panel, { attributes: true, attributeFilter: ['class'] });
    }

    // On the document rather than the panel: the panel can be open while focus is
    // still on the icon that opened it.
    document.addEventListener('keydown', function (event) {
      var escape = event.key === 'Escape' || event.key === 'Esc' || event.keyCode === 27;
      if (!escape || !isOpen()) return;
      if (typeof window.toggleClassSearch === 'function') window.toggleClassSearch();
      else panel.classList.remove('active');
      opener.focus();
    });
  }

  enhanceHeaderSearch();

  /* ------------------------------------------------------------------------
     Qikify BContact forms — WCAG 3.3.1 (A) Error Identification,
     2.5.3 (A) Label in Name and 1.3.5 (AA) Identify Input Purpose.

     The forms are injected at runtime by the app, so none of this can be done
     in the markup. Four separate defects are repaired here — note that the app
     validates in two different ways, and 1 and 4 below are one defect each way.

     1. Error messages. The fields carry the native `required` attribute, and
        the browser supplies that message in the *browser's* UI language, not
        the page's. A German page read in an English browser says "Please fill
        in this field". setCustomValidity replaces it with the storefront's own
        wording, which keeps the app's submit handling untouched — the field
        stays natively invalid, only the text changes.

     2. Accessible names in the wrong language. Every control carries a German
        aria-label alongside a translated <label for>, e.g. aria-label="Vorname*"
        against a visible "Nome*". aria-label wins, so the accessible name did
        not match the visible label — a 2.5.3 failure that also breaks voice
        control. Whatever translates the visible text does not touch attributes,
        so the fix is to drop the stale aria-label and let the real label speak.

     3. No autocomplete attributes, which 1.3.5 requires for identifiable
        fields, and no accessible name on the salutation radio group — which is
        also mandatory while announced as optional, so its required state is
        read back out of the app's own config.

     4. The app's own error summary, for the fields it validates itself rather
        than natively. It is built from the configured German labels, is English
        on every storefront, and is not announced at all. Detailed further down.

     Everything here is idempotent, because the form is a Vue app that
     re-renders and would otherwise drop the attributes.
     ------------------------------------------------------------------------ */

  var BCONTACT_FORM = 'form.bcontact-form';

  // Liquid's translate filter escapes its output for HTML, so the Italian
  // "Selezionare un'opzione." reaches this file as "Selezionare un&#39;opzione."
  // and would be read out, and shown, with the entity intact. These strings all
  // end up as plain text — in setCustomValidity, in textContent — so they have
  // to be decoded first. A textarea is the safe way: its content is text, never
  // parsed as markup, so no element or handler in the string can come alive.
  function decodeEntities(text) {
    if (typeof text !== 'string' || text.indexOf('&') < 0) return text;

    var box = document.createElement('textarea');
    box.innerHTML = text;
    return box.value;
  }

  var FORM_STRINGS = (function () {
    var raw = (window.themeA11y && window.themeA11y.formStrings) || {};
    var strings = {};

    for (var key in raw) {
      if (Object.prototype.hasOwnProperty.call(raw, key)) {
        strings[key] = decodeEntities(raw[key]);
      }
    }
    return strings;
  })();

  // Shopify names these fields contact[Vorname] and contact[name], first name
  // and surname, so `name` maps to family-name rather than the whole name.
  var AUTOCOMPLETE_BY_FIELD = {
    vorname: 'given-name',
    name: 'family-name',
    nachname: 'family-name',
    firma: 'organization',
    plz: 'postal-code',
    ort: 'address-level2',
    land: 'country-name',
    telefon: 'tel',
    email: 'email'
  };

  function bcontactForm(el) {
    return closest(el, BCONTACT_FORM);
  }

  function fieldKey(el) {
    var name = el.getAttribute && el.getAttribute('name');
    var match = name && name.match(/^contact\[(.+)\]$/);
    return match ? match[1].toLowerCase() : '';
  }

  function autocompleteFor(key) {
    if (AUTOCOMPLETE_BY_FIELD[key]) return AUTOCOMPLETE_BY_FIELD[key];
    // "Straße & Nr." — matched by prefix so the umlaut encoding cannot matter.
    if (key.indexOf('stra') === 0) return 'street-address';
    return '';
  }

  function labelTextFor(el) {
    // The generated ids contain "[", "&" and ".", so querySelector on a
    // for-attribute is not safe here. el.labels avoids escaping entirely.
    var labels = el.labels;
    if (!labels || !labels.length) return '';

    var text = '';
    for (var i = 0; i < labels.length; i++) {
      text += ' ' + (labels[i].textContent || '');
    }
    return text.replace(/\s+/g, ' ').trim();
  }

  function messageFor(el) {
    var validity = el.validity;
    if (!validity) return '';

    if (validity.valueMissing) {
      if (el.type === 'checkbox') return FORM_STRINGS.required_checkbox || '';
      if (el.type === 'radio' || el.tagName === 'SELECT') {
        return FORM_STRINGS.required_choice || '';
      }
      return FORM_STRINGS.required || '';
    }
    if (validity.typeMismatch && el.type === 'email') {
      return FORM_STRINGS.email || '';
    }
    // Anything else keeps the browser's own message rather than a vague one.
    return '';
  }

  function refreshValidity(el) {
    if (!el.setCustomValidity || !el.validity) return;

    // Clearing first lets the native state recompute; a stale custom message
    // would otherwise keep the field invalid after it has been filled in.
    el.setCustomValidity('');

    var message = messageFor(el);
    if (message) el.setCustomValidity(message);
    if (el.validity.valid) el.removeAttribute('aria-invalid');
  }

  /* Which fields the app treats as mandatory.

     It enforces `required` from its own configuration whether or not it renders
     the attribute, and for the salutation group it renders neither `required`
     nor `aria-required` — so that group is mandatory but announced as optional,
     and the first a screen reader hears of it is a failed submit. The config is
     in the page as window.qcfData, keyed by the numeric id that also ends the
     form's own id, so the truth can simply be read out of it.

     Only `=== true` counts. Fields made mandatory by a rule elsewhere, such as
     the address when the trade-partner box is ticked, are left alone rather than
     guessed at. */
  function requiredFields(form) {
    var entries = window.qcfData && window.qcfData.entries;
    var match = form.id && form.id.match(/(\d+)$/);
    var required = {};

    if (!entries || !entries.length || !match) return required;

    for (var i = 0; i < entries.length; i++) {
      if (String(entries[i].id) !== match[1]) continue;

      var fields = (entries[i].data && entries[i].data.fields) || [];
      for (var j = 0; j < fields.length; j++) {
        var name = fields[j].name || fields[j].formName;
        if (name && fields[j].required === true) required[String(name).toLowerCase()] = true;
      }
    }
    return required;
  }

  function enhanceControl(el, required) {
    var key = fieldKey(el);

    // An aria-label overrides the visible label, so remove it when a real
    // label exists, and remove it when it is empty and doing nothing.
    var ariaLabel = el.getAttribute('aria-label');
    if (ariaLabel !== null && (!ariaLabel.replace(/\s/g, '') || labelTextFor(el))) {
      el.removeAttribute('aria-label');
    }

    if (!el.getAttribute('autocomplete')) {
      var token = autocompleteFor(key);
      if (token) el.setAttribute('autocomplete', token);
    }

    // Radios are handled on their group instead: aria-required is not a
    // supported attribute of the radio role, only of radiogroup.
    if (required[key] && el.type !== 'radio' && el.getAttribute('aria-required') !== 'true') {
      el.setAttribute('aria-required', 'true');
    }

    refreshValidity(el);
  }

  function enhanceRadioGroups(form, required) {
    var fields = form.querySelectorAll('.bcontact-field--radio');

    for (var i = 0; i < fields.length; i++) {
      var group = fields[i].querySelector('.bcontact-radio-checkbox-group');
      if (!group) continue;

      // radiogroup rather than group, because only radiogroup carries
      // aria-required. The radios sit inside wrapper divs rather than directly
      // inside it, which every screen reader tested resolves by descent.
      var mandatory = !!required[fieldKey(group.querySelector('input'))];
      group.setAttribute('role', mandatory ? 'radiogroup' : 'group');

      if (mandatory) group.setAttribute('aria-required', 'true');
      else group.removeAttribute('aria-required');

      if (group.getAttribute('aria-labelledby') || group.getAttribute('aria-label')) continue;

      var heading = fields[i].querySelector('.bcontact-field-heading');
      var headingText = heading && heading.textContent.replace(/\s/g, '');

      if (headingText) {
        if (!heading.id) {
          heading.id = 'bcontact-group-label-' + i + '-' + (form.id || 'form');
        }
        group.setAttribute('aria-labelledby', heading.id);
      } else if (FORM_STRINGS.salutation && fieldKey(group.querySelector('input')) === 'gender') {
        // This group renders no heading at all, so without a name a screen
        // reader announces only "Il Sig., 1 of 3".
        group.setAttribute('aria-label', FORM_STRINGS.salutation);
      }
    }
  }

  /* ------------------------------------------------------------------------
     The app's own error summary, which handles every field it validates itself
     rather than through the native `required` attribute.

     It builds one sentence from the form's configuration —
     `"${labels}" ${field_message} ${required_error_message}` — and that has two
     defects which cannot be configured away:

       * the labels are the configured German ones, and checkbox and radio
         fields keep their text in the choice rather than the label, so an
         unticked consent box yields the literal `"" field is required`;
       * the three strings fall back to English on every storefront.

     Each failing field is marked .bcontact-field--error, which is signal enough
     to rebuild the sentence from the *visible* labels in the current language.

     The app also sets no aria-invalid, no aria-describedby and has no live
     region anywhere, so none of the failure reaches a screen reader: the
     summary appears silently, above a form the user has already tabbed past.
     ------------------------------------------------------------------------ */

  var ERROR_SUMMARY = '.bcontact-error';
  var ERROR_FIELD = '.bcontact-field--error';
  // The app's untranslated fallback, used when it blames no field in particular.
  var APP_GENERIC_ERROR = /^Oops!/;
  var LIVE_REGION_ID = 'a11y-form-alert';

  function collapse(text) {
    return (text || '').replace(/\s+/g, ' ').trim();
  }

  // A trailing asterisk is the required marker. It reads as noise in a sentence
  // that already says the field is required, and screen readers announce it.
  function trimMarker(text) {
    return collapse(text).replace(/[\s*]+$/, '');
  }

  function fieldName(wrapper) {
    var heading = wrapper.querySelector('.bcontact-field-heading');
    if (heading && trimMarker(heading.textContent)) {
      return trimMarker(heading.textContent);
    }

    // Checkbox and radio fields usually render no heading. A lone choice names
    // the field well enough, being the consent sentence itself; several do not,
    // so fall back to the name given to the group above.
    var choices = wrapper.querySelectorAll(
      '.bcontact-custom-checkbox-label, .bcontact-custom-radio-label'
    );
    if (choices.length === 1) return trimMarker(choices[0].textContent);

    var group = wrapper.querySelector('.bcontact-radio-checkbox-group');
    return group ? collapse(group.getAttribute('aria-label')) : '';
  }

  function fieldIsEmpty(wrapper) {
    var controls = wrapper.querySelectorAll('input, select, textarea');
    var found = false;

    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (el.type === 'hidden') continue;
      found = true;

      if (el.type === 'checkbox' || el.type === 'radio') {
        if (el.checked) return false;
      } else if (collapse(el.value)) {
        return false;
      }
    }
    return found;
  }

  function summaryMessage(form) {
    var wrappers = form.querySelectorAll(ERROR_FIELD);
    var missing = [];
    var invalid = [];
    var parts = [];

    for (var i = 0; i < wrappers.length; i++) {
      var name = fieldName(wrappers[i]);
      if (!name) continue;
      (fieldIsEmpty(wrappers[i]) ? missing : invalid).push(name);
    }

    if (missing.length && FORM_STRINGS.errors_required) {
      parts.push(FORM_STRINGS.errors_required.replace('%%FIELDS%%', missing.join(', ')));
    }
    if (invalid.length && FORM_STRINGS.errors_invalid) {
      parts.push(FORM_STRINGS.errors_invalid.replace('%%FIELDS%%', invalid.join(', ')));
    }
    return parts.join(' ');
  }

  function liveRegion() {
    if (!document.body) return null;

    var region = document.getElementById(LIVE_REGION_ID);
    if (region) return region;

    region = document.createElement('div');
    region.id = LIVE_REGION_ID;
    region.className = 'visually-hidden';
    // The node is added empty and filled later. role="alert" on a node inserted
    // together with its text is frequently missed, whereas a change inside one
    // already in the document is announced.
    region.setAttribute('role', 'alert');
    document.body.appendChild(region);
    return region;
  }

  function announce(text) {
    var region = liveRegion();
    if (region && collapse(region.textContent) !== text) region.textContent = text;
  }

  // Derived from the form rather than read off the summary, because the summary
  // is destroyed on a successful validation and the id would then be unknowable
  // — leaving every field pointing at an element that no longer exists.
  function summaryId(form) {
    return 'bcontact-error-' + (form.id || 'summary');
  }

  function describeErrors(form, active) {
    var controls = form.querySelectorAll('input, select, textarea');
    var id = summaryId(form);

    for (var i = 0; i < controls.length; i++) {
      var el = controls[i];
      if (el.type === 'hidden') continue;

      var errored = active && !!closest(el, ERROR_FIELD);
      var ids = (el.getAttribute('aria-describedby') || '').split(/\s+/);
      var kept = [];

      // Rebuilt rather than appended to, so nothing accumulates across submits.
      for (var j = 0; j < ids.length; j++) {
        if (ids[j] && ids[j] !== id) kept.push(ids[j]);
      }

      if (errored) {
        kept.push(id);
        el.setAttribute('aria-invalid', 'true');
      } else if (el.validity && el.validity.valid) {
        el.removeAttribute('aria-invalid');
      }

      if (kept.length) el.setAttribute('aria-describedby', kept.join(' '));
      else el.removeAttribute('aria-describedby');
    }
  }

  function enhanceErrorSummary(form) {
    var box = form.querySelector(ERROR_SUMMARY);

    if (!box) {
      describeErrors(form, false);
      // Cleared so that failing twice on the same field announces twice.
      announce('');
      return;
    }

    box.id = summaryId(form);

    var message = summaryMessage(form);
    if (!message && APP_GENERIC_ERROR.test(collapse(box.textContent))) {
      message = FORM_STRINGS.errors_generic || '';
    }

    // Vue owns this node and rewrites it on every failed submit, so the test is
    // against what is currently rendered rather than whether we have run.
    if (message && collapse(box.textContent) !== message) box.textContent = message;

    describeErrors(form, true);
    announce(collapse(box.textContent));
  }

  /* WCAG 1.4.3 (AA) — the app prints its radio and checkbox labels in #777 on
     white, 4.48:1. It sets that from a `form#bcontact-form-<id>` rule in a style
     block it injects at runtime, so an id beats any stylesheet selector we can
     write ahead of time and a later inline rule wins the tie. Setting the colour
     on the element is the one declaration that outranks it. */

  function fixLabelContrast(form) {
    var labels = form.querySelectorAll(
      '.bcontact-custom-radio-label, .bcontact-custom-checkbox-label'
    );

    for (var i = 0; i < labels.length; i++) {
      labels[i].style.setProperty('color', '#595959', 'important');
    }
  }

  function enhanceForm(form) {
    var controls = form.querySelectorAll('input, select, textarea');
    var required = requiredFields(form);

    for (var i = 0; i < controls.length; i++) {
      if (controls[i].type === 'hidden') continue;
      enhanceControl(controls[i], required);
    }
    enhanceRadioGroups(form, required);
    enhanceErrorSummary(form);
    fixLabelContrast(form);
  }

  function enhanceAllForms() {
    var forms = document.querySelectorAll(BCONTACT_FORM);
    for (var i = 0; i < forms.length; i++) enhanceForm(forms[i]);
  }

  // `invalid` does not bubble, hence the capture phase on all three.
  document.addEventListener(
    'invalid',
    function (event) {
      if (bcontactForm(event.target)) {
        event.target.setAttribute('aria-invalid', 'true');
      }
    },
    true
  );

  document.addEventListener(
    'input',
    function (event) {
      if (bcontactForm(event.target)) refreshValidity(event.target);
    },
    true
  );

  document.addEventListener(
    'change',
    function (event) {
      var form = bcontactForm(event.target);
      // Ticking a box can make other fields required, so refresh the whole form.
      if (form) enhanceForm(form);
    },
    true
  );

  enhanceAllForms();

  if (window.MutationObserver) {
    var rescan = null;
    var observer = new MutationObserver(function () {
      if (rescan) return;
      rescan = window.setTimeout(function () {
        rescan = null;
        enhanceAllForms();
      }, 50);
    });

    // childList only: watching attributes would re-fire on our own writes.
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  /* ------------------------------------------------------------------------
     Custom Product Builder configurator — WCAG 1.3.1 (A) Info and
     Relationships + WCAG 1.4.1 (A) Use of Color.

     The option names themselves are fine: the app gives every option an
     accessible name, so 2.4.4 passes as shipped. Two things around them do not.

     First, a group is a `.cpb-category-title` <div> followed by a container of
     loose `<div role="button">` options, with nothing connecting the two. A
     screen reader reads the 25 glaze swatches as 25 unrelated buttons. The title
     cannot be promoted to a real <h2> because the app owns and rebuilds the
     element, so it is given the heading role and the options' container is named
     from it.

     Second, the app writes each swatch's name into `.cpb-option-label` and then
     hides it with `display: none`, so a sighted user sees an unlabelled coloured
     square and no text anywhere records what they chose. The inline script in
     theme.liquid already solves this for the glaze and colour groups by appending
     the chosen value to the heading; the same treatment is extended here to the
     other swatch groups, which that script's FARBE/GLASUR test does not reach.

     `role="group"` rather than `"radiogroup"`: a radiogroup must own children with
     `role="radio"`, and these are toggle buttons carrying `aria-pressed`, which is
     a valid pattern by itself. Restyling them as radios would mean mirroring
     `aria-pressed` into `aria-checked` on every click, and the app mutates
     `aria-pressed` without firing its render event, so that mirror could go stale
     and announce the wrong selection. A named group of toggle buttons cannot.
     ------------------------------------------------------------------------ */

  var CPB_CATEGORY = '.cpb-category';
  var CPB_TITLE = '.cpb-category-title';
  var CPB_OPTION = '.cpb-option';
  var CPB_TITLE_TEXT = '.cpb-category-title__inner span';
  // Only the cpb-prefixed classes are stable; the rest are styled-components
  // hashes that change whenever the app is rebuilt.
  var CPB_OPTIONS_BOX = '.cpb-category-options-container';
  // The groups theme.liquid already relabels. Left alone to avoid two scripts
  // writing one element.
  var CPB_RELABELLED = /FARBE|GLASUR/;
  var CPB_BASE_TITLE = 'data-a11y-base-title';

  function cpbTitleText(title) {
    return title.querySelector(CPB_TITLE_TEXT) || title;
  }

  function cpbOptionName(option) {
    var label = option.querySelector('.cpb-option-label');
    var name = label ? label.textContent : '';

    if (!collapse(name)) {
      var image = option.querySelector('img');
      name = (image && image.getAttribute('alt')) || '';
    }
    return collapse(name);
  }

  function cpbShowsItsOwnNames(options) {
    // innerText excludes display:none text, so this is false exactly for the
    // swatch groups whose names the app hides.
    for (var i = 0; i < options.length; i++) {
      if (collapse(options[i].innerText)) return true;
    }
    return false;
  }

  function cpbSelectedNames(options) {
    var names = [];
    for (var i = 0; i < options.length; i++) {
      if (options[i].className.indexOf('cpb-active') < 0) continue;
      var name = cpbOptionName(options[i]);
      if (name) names.push(name);
    }
    return names;
  }

  function cpbEchoSelection(category, title, options) {
    var target = cpbTitleText(title);

    if (CPB_RELABELLED.test(collapse(target.textContent).toUpperCase())) return;
    if (cpbShowsItsOwnNames(options)) return;

    if (!title.getAttribute(CPB_BASE_TITLE)) {
      title.setAttribute(CPB_BASE_TITLE, collapse(target.textContent));
    }

    var base = title.getAttribute(CPB_BASE_TITLE);
    if (!base) return;

    var selected = cpbSelectedNames(options);
    var next = selected.length ? base + ' - ' + selected.join(', ') : base;

    // Guard the write. Assigning identical text still replaces the text node,
    // which the observer below would see as a change, calling this again forever.
    if (collapse(target.textContent) !== next) target.textContent = next;
  }

  function enhanceCpbCategory(category, index) {
    var title = category.querySelector(CPB_TITLE);
    var options = category.querySelectorAll(CPB_OPTION);
    if (!title || !options.length) return;

    if (!title.id) title.id = 'a11y-cpb-group-' + index;

    if (title.getAttribute('role') !== 'heading') {
      title.setAttribute('role', 'heading');
      // The product title is the h1 and the tabs below are h2, so the option
      // groups sit alongside those rather than under them.
      title.setAttribute('aria-level', '2');
    }

    var box = category.querySelector(CPB_OPTIONS_BOX) || options[0].parentElement;
    if (box && box.getAttribute('aria-labelledby') !== title.id) {
      box.setAttribute('role', 'group');
      box.setAttribute('aria-labelledby', title.id);
    }

    cpbEchoSelection(category, title, options);
  }

  function labelCpbPreview(root) {
    // The app stacks the preview out of <img> layers with no alt attribute, so a
    // screen reader reads out the CDN filename of each one. The configuration they
    // depict is already announced through the group headings above, so they carry
    // no information of their own.
    var layers = root.querySelectorAll('.cpb-render-konva img, .konvajs-content img');
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].getAttribute('alt') === null) layers[i].setAttribute('alt', '');
    }

    // The app also re-renders the product description inside its own panel, and
    // some descriptions contain an empty <img> left behind by the rich text
    // editor — no src, nothing to see, but still announced. Anything the theme
    // did not author and that arrives with no alt at all is neutralised here:
    // src-less tags leave the accessibility tree entirely, the rest become
    // decorative, since the surrounding description already carries the meaning.
    var strays = document.querySelectorAll('.cpb-product-builder img:not([alt])');
    for (var j = 0; j < strays.length; j++) {
      strays[j].setAttribute('alt', '');
      if (!strays[j].getAttribute('src')) strays[j].setAttribute('aria-hidden', 'true');
    }

    untabEmptyCpbLinks();
  }

  /* WCAG 2.4.3 (A) Focus Order and 2.4.4 (A) Link Purpose — the same description
     also carries an anchor with nothing inside it, wrapped around a data sheet
     link whose text sits outside the tag. It draws as a zero-width line, so it is
     a tab stop that shows nothing and announces only its URL. The same PDF is
     linked, properly named, in the product's Downloads tab. */
  function untabEmptyCpbLinks() {
    var links = document.querySelectorAll('.cpb-product-builder a[href]');

    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      var named =
        link.textContent.trim() ||
        link.getAttribute('aria-label') ||
        link.getAttribute('title') ||
        link.querySelector('img[alt]:not([alt=""])');
      if (named) continue;

      var rect = link.getBoundingClientRect();
      if (rect.width > 2 && rect.height > 2) continue;

      link.setAttribute('tabindex', '-1');
      link.setAttribute('aria-hidden', 'true');
    }
  }

  function enhanceConfigurator(root) {
    var categories = document.querySelectorAll(CPB_CATEGORY);
    for (var i = 0; i < categories.length; i++) {
      enhanceCpbCategory(categories[i], i);
    }
    if (root) labelCpbPreview(root);
    return categories.length;
  }

  function setUpConfigurator() {
    // Present in the server-rendered HTML on cpb_product pages, and the app fills
    // it in later. Absent everywhere else, so nothing below runs off these pages.
    var root = document.getElementById('product-builder');
    if (!root) return false;

    // The app fires this after every render; theme.liquid listens for it too.
    document.body.addEventListener('CPB_ON_RENDER_LAYERS', function () {
      enhanceConfigurator(root);
    });

    if (window.MutationObserver) {
      // Selecting an option only toggles a class, without necessarily firing the
      // event above, and the echoed heading has to follow the selection.
      var rescanCpb = null;
      var cpbObserver = new MutationObserver(function () {
        if (rescanCpb) return;
        rescanCpb = window.setTimeout(function () {
          rescanCpb = null;
          enhanceConfigurator(root);
        }, 100);
      });

      cpbObserver.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        // Only the selection class. Watching everything would re-fire on the
        // app's own style churn, and on nothing this code needs to react to.
        attributeFilter: ['class'],
      });
    }

    enhanceConfigurator(root);
    return true;
  }

  // Runs late enough in the body to see #product-builder, but the fallback keeps
  // that from being a requirement.
  if (!setUpConfigurator()) {
    document.addEventListener('DOMContentLoaded', setUpConfigurator);
  }

})();
